import React, { useMemo } from "react";
import {
	Box,
	Select,
	MenuItem,
	Typography,
	Grid,
	Divider,
	IconButton,
	Tooltip,
} from "@mui/material";
import { Refresh, ArrowBack } from "@mui/icons-material";

const renderGroupedMenuItems = (groups, emptyMessage) => {
	const hasItems = groups.some((group) => group.length > 0);

	if (!hasItems) {
		return [
			<MenuItem key="empty" disabled>
				{emptyMessage}
			</MenuItem>,
		];
	}

	return groups.flatMap((group, groupIndex) => [
		...(groupIndex > 0
			? [<Divider key={`divider-${groupIndex}`} component="li" />]
			: []),
		...group.map((dashboard) => (
			<MenuItem key={dashboard.id} value={dashboard.url}>
				{dashboard.name}
			</MenuItem>
		)),
	]);
};

const DashboardMenuColumn = ({
	label,
	value,
	onChange,
	menuItems,
	dashboardNamesByUrl,
	canGoBack,
	onBack,
	onRefresh,
}) => (
	<Grid item xs={12} md={6}>
		<Typography variant="subtitle1" sx={{ mb: 1 }}>
			{label}
		</Typography>
		<Box sx={{ display: "flex", alignItems: "center" }}>
			<Select
				fullWidth
				value={value}
				onChange={onChange}
				displayEmpty
				renderValue={(selected) => {
					if (!selected) {
						return (
							<Typography component="span" color="text.secondary">
								Select a dashboard
							</Typography>
						);
					}

					return dashboardNamesByUrl.get(selected) || selected;
				}}
			>
				{menuItems}
			</Select>
			<Tooltip arrow title={canGoBack ? "Back" : "No previous page in dashboard"}>
				<span>
					<IconButton onClick={onBack} disabled={!canGoBack}>
						<ArrowBack />
					</IconButton>
				</span>
			</Tooltip>
			<Tooltip arrow title="Refresh">
				<IconButton onClick={onRefresh}>
					<Refresh />
				</IconButton>
			</Tooltip>
		</Box>
	</Grid>
);

const OperativeDashboardMenus = ({
	operativeGroups,
	historyDashboards,
	uncategorized,
	selectedDashboard,
	onDashboardChange,
	canGoBack,
	onBack,
	onRefresh,
}) => {
	const operativeMenuGroups = useMemo(() => {
		const groups = operativeGroups
			.map((group) => [...group])
			.filter((group) => group.length > 0);

		if (uncategorized.length > 0 && groups.length > 0) {
			const lastGroup = groups[groups.length - 1];
			const existingUrls = new Set(lastGroup.map((dashboard) => dashboard.url));

			uncategorized.forEach((dashboard) => {
				if (!existingUrls.has(dashboard.url)) {
					lastGroup.push(dashboard);
					existingUrls.add(dashboard.url);
				}
			});
		} else if (uncategorized.length > 0) {
			groups.push([...uncategorized]);
		}

		return groups;
	}, [operativeGroups, uncategorized]);

	const operativeMenuItems = renderGroupedMenuItems(
		operativeMenuGroups,
		"No operative dashboards available"
	);

	const historyMenuItems =
		historyDashboards.length > 0 ? (
			historyDashboards.map((dashboard) => (
				<MenuItem key={dashboard.id} value={dashboard.url}>
					{dashboard.name}
				</MenuItem>
			))
		) : (
			<MenuItem disabled>No history dashboards available</MenuItem>
		);

	const operativeNamesByUrl = useMemo(() => {
		const namesByUrl = new Map();
		operativeMenuGroups.flat().forEach((dashboard) => {
			namesByUrl.set(dashboard.url, dashboard.name);
		});
		return namesByUrl;
	}, [operativeMenuGroups]);

	const historyNamesByUrl = useMemo(() => {
		const namesByUrl = new Map();
		historyDashboards.forEach((dashboard) => {
			namesByUrl.set(dashboard.url, dashboard.name);
		});
		return namesByUrl;
	}, [historyDashboards]);

	const operativeValue = operativeNamesByUrl.has(selectedDashboard)
		? selectedDashboard
		: "";
	const historyValue = historyNamesByUrl.has(selectedDashboard)
		? selectedDashboard
		: "";

	return (
		<Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
			<DashboardMenuColumn
				label="Operative Data View"
				value={operativeValue}
				onChange={onDashboardChange}
				menuItems={operativeMenuItems}
				dashboardNamesByUrl={operativeNamesByUrl}
				canGoBack={canGoBack}
				onBack={onBack}
				onRefresh={onRefresh}
			/>
			<DashboardMenuColumn
				label="History"
				value={historyValue}
				onChange={onDashboardChange}
				menuItems={historyMenuItems}
				dashboardNamesByUrl={historyNamesByUrl}
				canGoBack={canGoBack}
				onBack={onBack}
				onRefresh={onRefresh}
			/>
		</Grid>
	);
};

export default OperativeDashboardMenus;
